/**
 * Tabs component that transforms accordion rows into a tabbed interface.
 * Works with accordion blocks that have data-display-mode="tabs".
 *
 * Expected HTML structure:
 * <div class="accordion-block--tabs" data-display-mode="tabs" data-mobile-display="accordion|tabs">
 *   <nav class="tabs-tablist" role="tablist"></nav>
 *   <div class="tabs-panels"></div>
 *   <details-accordion>
 *     <details-custom data-tab-label="Tab 1">...</details-custom>
 *     <details-custom data-tab-label="Tab 2">...</details-custom>
 *   </details-accordion>
 * </div>
 */

class TabsInitializer {
  /** @type {WeakSet<HTMLElement>} */
  static #initialized = new WeakSet();

  /**
   * Initialize tabs on all accordion blocks with data-display-mode="tabs"
   */
  static initAll() {
    const containers = document.querySelectorAll('[data-display-mode="tabs"]:not(.tabs-initialized)');
    for (const container of containers) {
      if (container instanceof HTMLElement && !this.#initialized.has(container)) {
        new TabsController(container);
        this.#initialized.add(container);
      }
    }
  }
}

class TabsController {
  /** @type {HTMLElement} */
  #container;

  /** @type {HTMLElement|null} */
  #tablist;

  /** @type {HTMLElement|null} */
  #panelsContainer;

  /** @type {HTMLElement|null} */
  #accordion;

  /** @type {HTMLButtonElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  /** @type {number} */
  #activeIndex = 0;

  /** @type {MediaQueryList} */
  #mobileMediaQuery = window.matchMedia('(width < 64rem)');

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {'tabs'|'accordion'} */
  get #mobileDisplay() {
    return /** @type {'tabs'|'accordion'} */ (this.#container.dataset.mobileDisplay || 'accordion');
  }

  /** @type {boolean} */
  get #isMobile() {
    return this.#mobileMediaQuery.matches;
  }

  /** @type {boolean} */
  get #useAccordionOnMobile() {
    return this.#mobileDisplay === 'accordion';
  }

  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.#container = container;
    this.#tablist = container.querySelector('.tabs-tablist');
    this.#panelsContainer = container.querySelector('.tabs-panels');
    this.#accordion = container.querySelector('details-accordion');

    if (!this.#tablist || !this.#panelsContainer || !this.#accordion) {
      console.warn('TabsController: Missing required elements');
      return;
    }

    this.#transformAccordionToTabs();
    this.#setupAria();
    this.#activateTab(this.#activeIndex, false);
    this.#applyResponsiveMode();
    this.#container.classList.add('tabs-initialized');

    // Set up responsive behavior
    if (this.#useAccordionOnMobile) {
      this.#mobileMediaQuery.addEventListener('change', this.#handleMediaQueryChange, {
        signal: this.#abortController.signal,
      });
    }
  }

  /**
   * Transforms accordion rows into tab panels and creates tab buttons.
   */
  #transformAccordionToTabs() {
    if (!this.#tablist || !this.#panelsContainer || !this.#accordion) return;

    // Find all accordion rows (details-custom elements)
    const accordionRows = this.#accordion.querySelectorAll('details-custom');
    if (!accordionRows.length) return;

    for (let i = 0; i < accordionRows.length; i++) {
      const row = accordionRows[i];
      const label =
        row.dataset.tabLabel || row.querySelector('.details__summary')?.textContent?.trim() || `Tab ${i + 1}`;

      // Create tab button
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'tabs-tab';
      tab.setAttribute('role', 'tab');
      tab.textContent = label;
      tab.addEventListener('click', () => this.#activateTab(i), { signal: this.#abortController.signal });
      this.#tablist.appendChild(tab);
      this.#tabs.push(tab);

      // Create panel from accordion row content
      const panel = document.createElement('div');
      panel.className = 'tabs-panel';
      panel.setAttribute('role', 'tabpanel');

      // Clone the content from accordion row
      const contentWrapper = row.querySelector('.details__content');
      if (contentWrapper) {
        panel.innerHTML = contentWrapper.innerHTML;
      }

      this.#panelsContainer.appendChild(panel);
      this.#panels.push(panel);
    }

    // Bind keyboard navigation
    this.#tablist.addEventListener('keydown', (e) => this.#handleKeydown(e), { signal: this.#abortController.signal });
  }

  /**
   * Sets up ARIA roles and attributes.
   */
  #setupAria() {
    if (!this.#tablist) return;

    this.#tablist.setAttribute('role', 'tablist');

    for (let i = 0; i < this.#tabs.length; i++) {
      const tab = this.#tabs[i];
      const panel = this.#panels[i];
      if (!tab || !panel) continue;

      const id = this.#generateId(i);
      tab.id = `tab-${id}`;
      panel.id = `panel-${id}`;
      tab.setAttribute('aria-controls', panel.id);
      panel.setAttribute('aria-labelledby', tab.id);
    }
  }

  /**
   * Handles media query change events.
   */
  #handleMediaQueryChange = () => {
    this.#applyResponsiveMode();
  };

  /**
   * Applies the correct display mode based on viewport.
   */
  #applyResponsiveMode() {
    if (!this.#tablist || !this.#panelsContainer || !this.#accordion) return;

    if (this.#isMobile && this.#useAccordionOnMobile) {
      // Mobile accordion mode
      this.#container.classList.add('mobile-accordion-mode');
      this.#tablist.hidden = true;
      this.#panelsContainer.hidden = true;
      this.#accordion.hidden = false;
    } else {
      // Desktop tabs mode
      this.#container.classList.remove('mobile-accordion-mode');
      this.#tablist.hidden = false;
      this.#panelsContainer.hidden = false;
      this.#accordion.hidden = true;
      // Re-activate current tab
      this.#activateTab(this.#activeIndex, false);
    }
  }

  /**
   * Activates the tab at the given index.
   *
   * @param {number} index
   * @param {boolean} [animate=true]
   */
  #activateTab(index, animate = true) {
    if (index < 0 || index >= this.#tabs.length) return;

    // Update tab states
    for (let i = 0; i < this.#tabs.length; i++) {
      const tab = this.#tabs[i];
      const panel = this.#panels[i];
      const isActive = i === index;

      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.classList.toggle('tabs-tab--active', isActive);
      panel.hidden = !isActive;
    }

    this.#activeIndex = index;

    // Scroll tab into view
    if (animate) {
      this.#tabs[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }

  /**
   * Handles keyboard navigation (WAI-ARIA Tabs Pattern).
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.getAttribute('role') !== 'tab') return;

    const i = this.#tabs.indexOf(/** @type {HTMLButtonElement} */ (target));
    if (i === -1) return;

    // Handle Space/Enter to activate
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.#activateTab(i);
      return;
    }

    // Navigation map
    /** @type {Record<string, number>} */
    const navMap = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -i,
      End: this.#tabs.length - 1 - i,
    };

    const offset = navMap[event.key];
    if (offset !== undefined) {
      event.preventDefault();
      const nextIndex = (i + offset + this.#tabs.length) % this.#tabs.length;
      this.#tabs[nextIndex]?.focus();
      this.#activateTab(nextIndex);
    }
  }

  /**
   * Generates a unique ID for tab/panel pairs.
   *
   * @param {number} index
   * @returns {string}
   */
  #generateId(index) {
    const baseId =
      this.#container.id || this.#container.dataset.sectionId || `tabs-${Math.random().toString(36).slice(2, 8)}`;
    return `${baseId}-${index}`;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => TabsInitializer.initAll());
} else {
  TabsInitializer.initAll();
}

// Re-initialize when Shopify editor updates
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', () => TabsInitializer.initAll());
  document.addEventListener('shopify:block:select', () => TabsInitializer.initAll());
}

export { TabsInitializer, TabsController };
